// pages/Dashboard/Dashboard.js
import PropTypes from "prop-types"
import React, { useEffect, useState, useMemo } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  CardHeader,
  Button,
} from "reactstrap"
import { Link } from "react-router-dom"
import { useSelector } from "react-redux"
import { selectUser } from "store/features/auth/authSlice"
import DashboardLoader from "components/Loader/DashboardLoader"
import { orderApiService } from "services/orderApiService"
import { toast } from "sonner"
import TodayOrders from "components/Dashboard/TodayOrders"

const QwikMedicDashboard = props => {
  const user = useSelector(selectUser)

  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  document.title = "Dashboard | Qwik Medic"

  // Fetch orders
  const fetchOrders = async (showLoader = true) => {
    if (showLoader) setLoading(true)
    else setRefreshing(true)

    try {
      const data = await orderApiService.getAllOrders()
      setOrders(data)
    } catch (error) {
      toast.error("Failed to fetch orders")
      console.error("Error fetching orders:", error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    fetchOrders()
  }, [])

  // Parse date from DD/MM/YYYY format
  const parseDate = dateString => {
    if (!dateString) return null

    // Check if date is in DD/MM/YYYY format
    if (dateString.includes("/")) {
      const parts = dateString.split("/")
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10)
        const month = parseInt(parts[1], 10) - 1 // Months are 0-indexed
        const year = parseInt(parts[2], 10)
        return new Date(year, month, day)
      }
    }

    // Try parsing as ISO string or other format
    const parsed = new Date(dateString)
    return isNaN(parsed.getTime()) ? null : parsed
  }

  // Check if a date is today
  const isToday = dateString => {
    const orderDate = parseDate(dateString)
    if (!orderDate) return false

    const today = new Date()

    return (
      orderDate.getDate() === today.getDate() &&
      orderDate.getMonth() === today.getMonth() &&
      orderDate.getFullYear() === today.getFullYear()
    )
  }

  // Get today's orders
  const todaysOrders = useMemo(() => {
    if (!orders.length) return []

    return orders.filter(order => isToday(order.orderdate))
  }, [orders])

  // Calculate statistics
  const totalOrders = orders.length
  const totalAmount = orders.reduce(
    (sum, order) => sum + parseFloat(order.totalamount || 0),
    0
  )

  const pendingOrders = orders.filter(
    order =>
      order.orderstatus != "Cancelled" || order.orderstatus != "Delivered"
  ).length

  const cancelledOrders = orders.filter(
    order => order.orderstatus == "Cancelled"
  ).length

  const deliveredOrders = orders.filter(
    order => order.orderstatus == "Delivered"
  ).length

  const submittedOrders = orders.filter(
    order => order.orderstatus == "submitted"
  ).length

  // Today's statistics
  const todaysTotalAmount = todaysOrders.reduce(
    (sum, order) => sum + parseFloat(order.totalamount || 0),
    0
  )

  const todaysDeliveredOrders = todaysOrders.filter(
    order => order.orderstatus === "Delivered"
  ).length

  // Format currency
  const formatCurrency = amount => {
    return `৳${parseFloat(amount || 0).toLocaleString("en-BD")}`
  }

  if (loading) {
    return <DashboardLoader />
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="page-title-box">
            <Row className="align-items-center">
              <Col md={8}>
                <h6 className="page-title">Dashboard</h6>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item active">
                    Welcome to Qwik Medic Dashboard
                  </li>
                </ol>
              </Col>
              <Col md={4}>
                <div className="d-flex justify-content-end gap-2">
                  <Button
                    color="light"
                    onClick={() => fetchOrders(false)}
                    disabled={refreshing}
                    title="Refresh Data"
                  >
                    <i
                      className={`fas fa-sync-alt ${
                        refreshing ? "fa-spin" : ""
                      }`}
                    ></i>
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          {/* Statistics Cards */}
          <div className="row mb-3">
            <div className="col-md-3">
              <div className="card border-success border-opacity-25">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="text-muted mb-0">Total Orders</p>
                      <h4 className="mt-2">{totalOrders}</h4>
                    </div>
                    <div className="avatar-sm">
                      <span className="avatar-title bg-success rounded-circle">
                        <i className="fas fa-shopping-cart font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="card border-warning border-opacity-25">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="text-muted mb-0">Pending Orders</p>
                      <h4 className="mt-2">{pendingOrders}</h4>
                    </div>
                    <div className="avatar-sm">
                      <span className="avatar-title bg-warning rounded-circle">
                        <i className="fas fa-clock font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="card border-danger border-opacity-25">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="text-muted mb-0">Delivered Orders</p>
                      <h4 className="mt-2">{deliveredOrders}</h4>
                    </div>
                    <div className="avatar-sm">
                      <span className="avatar-title bg-success rounded-circle">
                        {/* <i className="fas fa-times-circle font-size-24"></i> */}
                        <i className="fas fa-check-circle font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="card border-primary border-opacity-25">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="text-muted mb-0">Total Amount</p>
                      <h4 className="mt-2">{formatCurrency(totalAmount)}</h4>
                    </div>
                    <div className="avatar-sm">
                      <span className="avatar-title bg-primary rounded-circle">
                        <i className="fas fa-money-bill-wave font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Today's Orders Section */}
          <Row>
            <Col xl={12}>
              <Card>
                <CardHeader>
                  <div className="d-flex justify-content-between align-items-center">
                    <CardTitle tag="h5" className="mb-0">
                      <i className="fas fa-calendar-day me-2 text-primary"></i>
                      Today's Orders (
                      {new Date().toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                      )
                      <span
                        className={`badge ms-2 ${
                          todaysOrders.length > 0
                            ? "bg-primary"
                            : "bg-secondary text-dark"
                        }`}
                      >
                        {todaysOrders.length} order
                        {todaysOrders.length !== 1 ? "s" : ""}
                      </span>
                    </CardTitle>
                    <div className="d-flex gap-2">
                      <Button
                        color="light"
                        size="sm"
                        onClick={() => fetchOrders(false)}
                        disabled={refreshing}
                        title="Refresh"
                      >
                        <i
                          className={`fas fa-sync-alt ${
                            refreshing ? "fa-spin" : ""
                          }`}
                        ></i>
                      </Button>

                      <Link to="/all-orders" className="btn btn-primary">
                        <i className="fas fa-list me-1"></i>
                        View All Orders
                      </Link>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <TodayOrders orders={todaysOrders} loading={refreshing} />

                  {todaysOrders.length > 0 && (
                    <div className="text-center mt-3">
                      <Link
                        to="/all-orders"
                        className="btn btn-outline-primary"
                      >
                        <i className="fas fa-list me-2"></i>
                        View All Orders
                      </Link>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

QwikMedicDashboard.propTypes = {
  t: PropTypes.any,
}

export default QwikMedicDashboard
